import * as Yup from 'yup';
import {validateRe2Pattern} from '../../../../util/util';

// Splits a stream's rows out to child owners. Each entry names how to recognise the
// child's rows (match) and which owner they belong to (owner_id).
//
//   pen          - the child owns pens in pen_dimension; rows in those pens are theirs.
//                  Needs no value: which pens is a pen_dimension change, not a config one.
//   file_pattern - regex matched against the source file name.
//   file_key     - exact key extracted from the file name by the provider's parser.
//   label        - exact (case-insensitive) match on the row's label column.
//   reassign     - no match: every row goes to one owner. An administrative override,
//                  offered on every stream whatever the provider's scheme.
//
// Omitting match is read as pen, so existing configs keep working unchanged.

// iYotah Solutions, the platform's own owner; id 10 in every environment. Its streams
// belong to devices serving unrelated farms, so their rules may name any owner rather
// than only children.
const IYOTAH_OWNER_ID = 10;

// Whether this owner may route rows to owners that are not its children.
const routesToAnyOwner = (ownerId) => Number(ownerId) === IYOTAH_OWNER_ID;

// provider.partition_scheme names the ways a provider's rows can be split. The stream
// editor uses this to offer only those match types, since a rule of any other type would
// be read by nothing.
const SCHEME_TO_MATCH = {
  PEN: 'pen',
  LABEL: 'label',
  FILE_PATTERN: 'file_pattern',
  FILE_KEY: 'file_key',
};

// The column holds one scheme for almost every provider, and a comma-separated list for
// the few that need more than one — EZfeed-Loads splits by pen and by label, so its
// streams need rules of both kinds. Tokens are returned as stored, not normalised: an
// unrecognised one must survive a round trip through the provider form rather than be
// silently dropped on save. schemeMatchTypes is what discards it.
const parsePartitionSchemes = (value) => {
  if (value === undefined || value === null || value === '') return [];
  const list = Array.isArray(value) ? value : String(value).split(',');
  return [...new Set(list.map((scheme) => String(scheme).trim()).filter(Boolean))];
};

// The match types a provider's schemes allow, in the order the schemes are listed.
// Unrecognised schemes contribute nothing.
const schemeMatchTypes = (value) =>
  parsePartitionSchemes(value).map((scheme) => SCHEME_TO_MATCH[scheme]).filter(Boolean);

const EXAMPLE_VALUES = {
  file_pattern: '^Location2',
  file_key: '2',
  label: 'location2',
};

// The seeded example has to match the provider's scheme, or it demonstrates a rule the
// provider cannot use. A provider with several schemes is seeded from the first: one
// example is enough to show the shape, and the editor offers the rest as match types.
const exampleForScheme = (scheme) => {
  const match = schemeMatchTypes(scheme)[0] || 'pen';
  const entry = {match, owner_id: 12345};
  if (EXAMPLE_VALUES[match] !== undefined) entry.value = EXAMPLE_VALUES[match];
  return JSON.stringify([entry], null, 2);
};

const examplePartitionConfigs = exampleForScheme(null);

// True when the field still holds an untouched example, whichever scheme produced it.
// Used to avoid saving the example as though it were real configuration. Multi-scheme
// providers seed from their first scheme, so the single-scheme examples cover them too.
const isExampleValue = (value) =>
  value === examplePartitionConfigs ||
  Object.keys(SCHEME_TO_MATCH).some((scheme) => value === exampleForScheme(scheme));

// provider.device_provider marks a PC Agent feed: the rows are pushed by an agent running on
// a machine at the owner's site rather than pulled on a schedule, so the stream belongs to
// one device and the editor has to ask which. The column is a MySQL boolean and reaches the
// browser as 0/1, but the provider form round-trips it as the strings 'true'/'false', so
// both spellings are read here.
const isDeviceProvider = (provider) => {
  const value = provider?.device_provider;
  if (typeof value === 'string') {
    const text = value.trim().toLowerCase();
    return text === 'true' || text === '1';
  }
  return Boolean(value);
};

// device_view exposes owner_name but not owner_id. owner.name is unique in the database and
// the view reaches it by joining on the device's own owner_id, so matching on the name picks
// out exactly the devices that owner has.
const devicesForOwner = (devices, owner) => {
  if (!Array.isArray(devices) || !owner?.name) return [];
  return devices.filter((device) => device.owner_name === owner.name);
};

// The name PC Agent gives a stream it registers itself - see createDeviceSource in ms-ngest,
// which builds `${deviceId} - ${owner.name} - ${source}` from the device's MAC address and
// the provider name, and uses it for the description too. A stream created here has to land
// on the same string: the agent looks its stream up by device and provider, and a second
// name for the same pair is a duplicate nobody can tell apart in the grid.
const deviceStreamName = (device, owner, provider) => {
  if (!device?.mac_address || !owner?.name || !provider?.name) return '';
  return `${device.mac_address} - ${owner.name} - ${provider.name}`;
};

// Streams that are not a PC Agent feed have no device to name them after, so they are
// numbered: one owner commonly has several streams from the same provider - a site apiece,
// or one per file drop. The number handed out is the lowest not already in use under this
// exact prefix, which is what keeps the result unique; data_stream.name has a UNIQUE key,
// and a collision is a 500 from the insert rather than anything the form could explain.
//
// Matching on the name prefix rather than on owner_id and provider_id is deliberate: the
// prefix is what two names would actually collide on, so a stream left under an old name
// after its owner or provider changed is still counted.
const nextInstanceNumber = (prefix, dataStreams, rowEditing) => {
  const numberIn = (name) =>
    typeof name === 'string' && name.startsWith(prefix) ? Number(name.slice(prefix.length)) : NaN;

  // A stream that already sits under this prefix keeps the number it has. Opening one and
  // saving it must not renumber it, and nor should the numbers shuffle when an earlier
  // stream is deleted.
  const own = numberIn(rowEditing?.name);
  if (Number.isInteger(own) && own > 0) return own;

  const taken = new Set();
  for (const stream of (Array.isArray(dataStreams) ? dataStreams : [])) {
    if (rowEditing?.id !== undefined && stream.id === rowEditing.id) continue;
    const number = numberIn(stream.name);
    if (Number.isInteger(number) && number > 0) taken.add(number);
  }

  let next = 1;
  while (taken.has(next)) next += 1;
  return next;
};

// The name of a stream, worked out from what the form has selected rather than typed by
// hand - so a name always says what the stream is, and two people setting up the same feed
// arrive at the same name.
//
//   PC Agent  <mac> - <owner> - <provider>   the string the agent registers itself
//   otherwise <provider> - <owner> - <n>     n numbering the owner's streams from that provider
//
// Empty until enough has been chosen to build it; the caller leaves the field alone until
// then rather than showing a half-built name.
const streamName = ({provider, owner, device, dataStreams, rowEditing}) => {
  if (!provider?.name || !owner?.name) return '';

  if (isDeviceProvider(provider)) return deviceStreamName(device, owner, provider);

  const prefix = `${provider.name} - ${owner.name} - `;
  return `${prefix}${nextInstanceNumber(prefix, dataStreams, rowEditing)}`;
};

// Options for the device picker. The MAC address leads because that is what the stream name
// is built from; the device's own name, when it has one, only helps tell two of them apart.
const deviceOptions = (devices) =>
  devices.map((device) => ({
    id: device.id,
    name: device.name ? `${device.mac_address} (${device.name})` : device.mac_address,
  }));

const MATCH_TYPES = ['pen', 'file_pattern', 'file_key', 'label', 'reassign'];

const VALUELESS_MATCHES = ['pen', 'reassign'];

// What the rule's value means, per match type. A bare "Value" gives no clue that a
// file_key rule wants the key the provider's regex pulls out of the file name.
const VALUE_LABELS = {
  file_pattern: 'File name pattern',
  file_key: 'File key',
  label: 'Label value',
};

const VALUE_PLACEHOLDERS = {
  file_pattern: '^Wardsprings',
  file_key: '2',
  label: 'location2',
};

const MATCH_LABELS = {
  pen: 'Pen',
  file_pattern: 'File name pattern',
  file_key: 'File name key',
  label: 'Label',
  reassign: 'All rows (override)',
};

// Turns a stored partition_configs value into editable rows. Understands every shape in
// the database: the explicit form, the legacy array of {owner_id} (read as pen), the
// legacy array of {label_value, owner_id}, and the legacy object of {key: owner_id}
// (read as file_key). Returns ok:false when the value cannot be understood, so the caller
// can fall back to raw JSON editing rather than silently discarding it.
const parsePartitionConfigs = (value) => {
  if (value === undefined || value === null || value === '') return {ok: true, rows: []};

  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch (err) {
      return {ok: false, rows: []};
    }
  }

  if (Array.isArray(parsed)) {
    const rows = parsed.map((entry) => ({
      match: entry.match ?? (entry.label_value !== undefined ? 'label' : 'pen'),
      value: entry.value ?? entry.label_value ?? '',
      owner_id: entry.owner_id ?? '',
    }));
    return {ok: rows.every((r) => MATCH_TYPES.includes(r.match)), rows};
  }

  if (typeof parsed === 'object') {
    return {
      ok: true,
      rows: Object.entries(parsed).map(([key, ownerId]) => ({
        match: 'file_key',
        value: key,
        owner_id: ownerId,
      })),
    };
  }

  return {ok: false, rows: []};
};

// Always writes the explicit form. Valueless matches omit `value` entirely.
const serializePartitionConfigs = (rows) => {
  if (!rows.length) return '';
  return JSON.stringify(
    rows.map((row) => ({
      match: row.match,
      ...(VALUELESS_MATCHES.includes(row.match) ? {} : {value: row.value}),
      owner_id: Number.isInteger(row.owner_id) ? row.owner_id : parseInt(row.owner_id, 10),
    })),
    null,
    2
  );
};

// Returns an error string, or null when the value is acceptable. Kept deliberately
// permissive about shape — the legacy array-of-{owner_id} and object-of-{key: owner_id}
// forms are both still live in the database and must remain editable.
const validatePartitionConfigs = (value) => {
  if (value === undefined || value === null || value === '') return null;

  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch (err) {
      return 'Not valid JSON.';
    }
  }

  // Legacy object form, {"1": 12345}: keys are file keys, values are owner ids.
  if (!Array.isArray(parsed)) {
    if (typeof parsed !== 'object') return 'Must be an array of entries, or an object of {key: owner_id}.';
    const badValue = Object.entries(parsed).find(([, ownerId]) => !Number.isInteger(ownerId));
    return badValue ? `Owner id for key "${badValue[0]}" must be a whole number.` : null;
  }

  if (parsed.length === 0) return 'Must contain at least one entry, or be left empty.';

  for (const [i, entry] of parsed.entries()) {
    const at = `Entry ${i + 1}`;

    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) return `${at} must be an object.`;
    if (!Number.isInteger(entry.owner_id)) return `${at} needs an owner_id that is a whole number.`;

    const match = entry.match ?? (entry.label_value !== undefined ? 'label' : 'pen');
    if (!MATCH_TYPES.includes(match)) {
      return `${at} has match "${entry.match}". Use one of: ${MATCH_TYPES.join(', ')}.`;
    }

    const matchValue = entry.value ?? entry.label_value;
    if (VALUELESS_MATCHES.includes(match)) {
      if (matchValue !== undefined) return `${at} matches on ${match}, which takes no value.`;
    } else if (matchValue === undefined || matchValue === '') {
      return `${at} matches on ${match}, so it needs a value.`;
    }

    // Judged as BigQuery's RE2 engine would, not as the browser would: the two disagree in
    // both directions, so a plain `new RegExp` check both admits patterns the pipeline
    // rejects and rejects patterns the pipeline accepts.
    if (match === 'file_pattern') {
      const problem = validateRe2Pattern(matchValue);
      if (problem) return `${at}: ${problem}`;
    }
  }

  return null;
};

const generateValidationSchema = (fields) => {
  let schemaObject = {
    name: Yup.string().required('Required')
  };

  if (!fields) return schemaObject;

  for (let field of fields) {
    if (field.required || field.type !== 'string') {
      // Yup.string() would cast an array to "a,b" and pass for the wrong reason.
      if (field.type === 'array') {
        schemaObject[field.name] = field.required
          ? Yup.array().min(1, 'Required')
          : Yup.array();
      } else {
        schemaObject[field.name] = Yup.string().required('Required');
      }
      schemaObject[field.name].type = field.type;
    }
  }
  return schemaObject;
};

const prettifyProperties = (properties) => {
  if (!properties) return null;

  let newFields = {};
  for (let field of properties) {
    // Array properties stay arrays through the form. A stored string is kept as one entry
    // rather than split — site names contain commas.
    let value;
    if (field.type === 'array') {
      value = Array.isArray(field.value) ? field.value : (field.value ? [field.value] : []);
    } else {
      value = field.value ? field.value : '';
    }
    Object.defineProperty(newFields, field.name, {value, enumerable: true});
  }
  return newFields;
};

export default {
  isDeviceProvider,
  devicesForOwner,
  deviceStreamName,
  deviceOptions,
  streamName,
  nextInstanceNumber,
  generateValidationSchema,
  prettifyProperties,
  examplePartitionConfigs,
  validatePartitionConfigs,
  parsePartitionConfigs,
  serializePartitionConfigs,
  exampleForScheme,
  isExampleValue,
  parsePartitionSchemes,
  schemeMatchTypes,
  routesToAnyOwner,
  IYOTAH_OWNER_ID,
  SCHEME_TO_MATCH,
  MATCH_TYPES,
  VALUELESS_MATCHES,
  MATCH_LABELS,
  VALUE_LABELS,
  VALUE_PLACEHOLDERS,
};